// Match supplements to clinical algorithm results & altered biomarkers.
// Returns { topPicks: [...with reason], byAlgorithm: { [algoKey]: [items] }, popular: [...] }

import { SUPPLEMENTS } from "@/lib/supplements";

const findByName = (q) =>
  SUPPLEMENTS.find((s) => s.name.toLowerCase().includes(q.toLowerCase()));

const findAllByName = (q) =>
  SUPPLEMENTS.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));

// Returns list of supplements (deduped by uid) given an algorithm key/response.
function pickForAlgo(key, algo) {
  if (!algo || !algo.SUCCESS) return [];
  const list = [];

  const isAltered = (s) =>
    s &&
    typeof s.response === "string" &&
    s.response &&
    !/^[A-Z]+0$/i.test(s.response); // e.g. L0, A0, CM0, B0, VD0

  switch (key) {
    case "glucose":
      if (isAltered(algo)) {
        const a = findByName("Berberina");
        const b = findByName("Myo-inositol");
        const c = findByName("Magnesio Bisglicinato");
        const d = findByName("Omega 3 (1300mg)");
        [a, b, c, d].forEach((x) => x && list.push(x));
      }
      break;
    case "cholesterol":
      if (isAltered(algo)) {
        const a = findByName("Levadura Roja de Arroz + COQ10");
        const b = findByName("Omega 3 (1300mg)");
        const c = findByName("COQ10 Ubiquinol");
        const d = findByName("Resveratrol (250mg)");
        [a, b, c, d].forEach((x) => x && list.push(x));
      }
      break;
    case "vitaminD":
      if (algo.response && algo.response !== "VD0") {
        const a = findByName("Vitamina D3-K2");
        const b = findByName("Vitamina D (4000IU)");
        const c = findByName("Vitamina D3 (2000UI)");
        [a, b, c].forEach((x) => x && list.push(x));
      }
      break;
    case "magnesium":
      if (algo.response && algo.response !== "Mg0") {
        const a = findByName("Magnesio Bisglicinato");
        const b = findByName("Magnesio + Vitamina B6");
        [a, b].forEach((x) => x && list.push(x));
      }
      break;
    case "zinc":
      if (algo.response && algo.response !== "Zn0") {
        const a = findByName("Zinc Bisglicinato");
        const b = findByName("Zinc Citrato");
        [a, b].forEach((x) => x && list.push(x));
      }
      break;
    case "anemia":
      if (isAltered(algo)) {
        const a = findByName("Hierro (25mg)");
        const b = findByName("Vitamina B12 (1000mcg)");
        const c = findByName("Ácido Fólico (800mcg)");
        [a, b, c].forEach((x) => x && list.push(x));
      }
      break;
    case "liver":
      if (isAltered(algo)) {
        const a = findByName("Cardo Mariano");
        const b = findByName("Cúrcuma (600mg)");
        [a, b].forEach((x) => x && list.push(x));
      }
      break;
    case "vitaminB":
      if (isAltered(algo)) {
        const a = findByName("Complejo Vitamina B");
        const b = findByName("Vitamina B12 (1000mcg)");
        [a, b].forEach((x) => x && list.push(x));
      }
      break;
    case "cametabolism":
      if (isAltered(algo)) {
        const a = findByName("Calcio + Vitamina D3");
        const b = findByName("Vitamina D3-K2");
        [a, b].forEach((x) => x && list.push(x));
      }
      break;
    default:
      break;
  }
  return list;
}

// Friendly reason text keys (translated by UI)
const REASON_KEYS = {
  glucose: "supplements.reason.glucose",
  cholesterol: "supplements.reason.cholesterol",
  vitaminD: "supplements.reason.vitaminD",
  magnesium: "supplements.reason.magnesium",
  zinc: "supplements.reason.zinc",
  anemia: "supplements.reason.anemia",
  liver: "supplements.reason.liver",
  vitaminB: "supplements.reason.vitaminB",
  cametabolism: "supplements.reason.cametabolism",
};

export function getSupplementRecommendations(algorithmResult = {}) {
  const byAlgorithm = {};
  const all = [];

  for (const key of Object.keys(algorithmResult)) {
    const items = pickForAlgo(key, algorithmResult[key]);
    if (items.length) {
      // Cap to 2 per algorithm to diversify top picks across reasons
      const capped = items.slice(0, 2);
      byAlgorithm[key] = capped.map((s) => ({ ...s, reasonKey: REASON_KEYS[key] }));
      capped.forEach((s) =>
        all.push({ ...s, reasonKey: REASON_KEYS[key], algoKey: key })
      );
    }
  }

  // Top picks: dedupe by uid keeping first match (highest priority algo first)
  const seen = new Set();
  const topPicks = [];
  for (const item of all) {
    if (seen.has(item.uid)) continue;
    seen.add(item.uid);
    topPicks.push(item);
  }

  // Fallback: when nothing altered, suggest popular foundational stack
  if (topPicks.length === 0) {
    const fallback = [
      findByName("Omega 3 (1300mg)"),
      findByName("Vitamina D3-K2"),
      findByName("Magnesio Bisglicinato"),
      findByName("Vitamina C Liposomada"),
    ].filter(Boolean);
    fallback.forEach((s) => topPicks.push({ ...s, reasonKey: "supplements.reason.foundation" }));
  }

  const popular = SUPPLEMENTS.filter((s) => s.popular).slice(0, 6);

  return { topPicks: topPicks.slice(0, 6), byAlgorithm, popular };
}

export { findByName, findAllByName };
